// StudentDashboard.js
import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Scanner } from "@yudiel/react-qr-scanner";
import "./StudentDashboard.css";

export default function StudentDashboard() {
  const { studentUser, logoutStudent } = useAuth();
  const [timetable, setTimetable] = useState({});
  const [periods, setPeriods] = useState([]);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [faceModalOpen, setFaceModalOpen] = useState(false);
  const [scannedData, setScannedData] = useState(null);
  const [sessionCode, setSessionCode] = useState("");
  const [attendanceStats, setAttendanceStats] = useState({});

  // ✅ Attendance Panel states
  const [viewAttendanceOpen, setViewAttendanceOpen] = useState(false);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentsList, setStudentsList] = useState([]);
  const [attendanceRecords, setAttendanceRecords] = useState([]);

  // ✅ Timetable panel
  const [viewTimetableOpen, setViewTimetableOpen] = useState(false);

  // ✅ Full attendance table
  const [attendanceTable, setAttendanceTable] = useState([]);
  const [sessionDates, setSessionDates] = useState([]);

  // ✅ Subjects from timetable
  const [subjects, setSubjects] = useState([]);

  const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  // ✅ FIXED: Support both string and object course
  const generateCourseKey = (deptName, course) => {
    if (!course) return deptName;
    if (typeof course === "string") return `${deptName}_${course}`;
    if (typeof course === "object" && course.code) return `${deptName}_${course.code}`;
    return deptName;
  };

  useEffect(() => {
    if (studentUser && studentUser.departmentName && studentUser.course) {
      const key = generateCourseKey(studentUser.departmentName, studentUser.course);

      const savedTimetable =
        JSON.parse(localStorage.getItem(`${key}_timetable`)) || {};
      const savedPeriods =
        JSON.parse(localStorage.getItem(`${key}_periods`)) || [];

      setTimetable(savedTimetable);
      setPeriods(savedPeriods);

      // 🔥 Extract subjects directly from timetable
      let timetableSubjects = [];
      Object.keys(savedTimetable).forEach((day) => {
        Object.values(savedTimetable[day] || {}).forEach((cell) => {
          if (cell?.subject) {
            timetableSubjects.push(cell.subject);
          }
        });
      });
      timetableSubjects = [...new Set(timetableSubjects)];
      setSubjects(timetableSubjects);
    }

    if (studentUser) {
      const savedStats =
        JSON.parse(localStorage.getItem(`studentAttendance_${studentUser.id}`)) ||
        {};
      setAttendanceStats(savedStats);
    }
    setViewTimetableOpen(true);
    setViewAttendanceOpen(false);
  }, [studentUser]);

  // ✅ Mark Attendance
  const markAttendance = (subject, sessionKey) => {
    if (!studentUser) return;
    let sessionData = JSON.parse(localStorage.getItem(`attendance_${sessionKey}`));
    if (!sessionData) {
      alert("❌ Invalid or expired session. Ask your faculty to generate a new QR.");
      return;
    }
    if (!sessionData.present.includes(studentUser.id)) {
      sessionData.present.push(studentUser.id);
      localStorage.setItem(`attendance_${sessionKey}`, JSON.stringify(sessionData));
    }
    const studentKey = `studentAttendance_${studentUser.id}`;
    const studentData = JSON.parse(localStorage.getItem(studentKey)) || {};
    if (!studentData[subject]) {
      studentData[subject] = { present: 0, total: 0, percentage: 0 };
    }
    if (studentData[subject].lastSession !== sessionKey) {
      studentData[subject].present += 1;
      studentData[subject].total += 1;
      studentData[subject].lastSession = sessionKey;
      studentData[subject].percentage =
        (studentData[subject].present / studentData[subject].total) * 100;
    }
    localStorage.setItem(studentKey, JSON.stringify(studentData));
    setAttendanceStats(studentData);

    // 🔥 Refresh the table instantly if student is viewing this subject
    if (selectedSubject === subject) {
      buildAttendanceTable(subject);
    }
  };

  // ✅ QR Scanner
  const handleScan = (result) => {
    if (result && result[0]?.rawValue) {
      try {
        const parsed = JSON.parse(result[0].rawValue);
        setScannedData(parsed);
        setQrModalOpen(false);
        setFaceModalOpen(true);
      } catch (err) {
        console.error("Invalid QR Data:", err);
      }
    }
  };

  // ✅ Session Code
  const handleSessionCodeSubmit = () => {
    if (sessionCode.trim() !== "") {
      const sessionData = JSON.parse(localStorage.getItem(`attendance_${sessionCode}`));
      if (sessionData && sessionData.subject) {
        markAttendance(sessionData.subject, sessionCode);
        alert(`✅ Attendance marked for subject: ${sessionData.subject}`);
      } else {
        alert("❌ Invalid or expired session code!");
      }
      setSessionCode("");
      setQrModalOpen(false);
    }
  };

  // ✅ Face Recognition
  const startFaceRecognition = () => {
    if (scannedData) {
      markAttendance(scannedData.subject, scannedData.sessionId);
      alert(`✅ Attendance marked!\nSubject: ${scannedData.subject}`);
    }
    setFaceModalOpen(false);
  };

  // ✅ Load students for same course
  const loadStudentsForCourse = () => {
    const allStudents = JSON.parse(localStorage.getItem("students")) || [];
    const filtered = allStudents.filter(
      (s) =>
        s.departmentName === studentUser.departmentName &&
        (typeof s.course === "string"
          ? s.course === (typeof studentUser.course === "string" ? studentUser.course : studentUser.course.code)
          : s.course.code === (typeof studentUser.course === "string" ? studentUser.course : studentUser.course.code))
    );
    setStudentsList(filtered);
  };

  // ✅ Load attendance records
  const loadAttendanceRecords = (subject, studentId) => {
    const sessionKeys = Object.keys(localStorage).filter((k) =>
      k.startsWith("attendance_")
    );
    let records = [];
    sessionKeys.forEach((key) => {
      const session = JSON.parse(localStorage.getItem(key));
      if (session && session.subject === subject) {
        const status = session.present.includes(studentId) ? "Present" : "Absent";
        records.push({ date: session.date, status });
      }
    });
    setAttendanceRecords(records);
  };

  // ✅ Build full attendance table
  const buildAttendanceTable = (subject) => {
    const sessionKeys = Object.keys(localStorage).filter((k) =>
      k.startsWith("attendance_")
    );
    let dates = [];
    let sessionMap = {};
    sessionKeys.forEach((key) => {
      const session = JSON.parse(localStorage.getItem(key));
      if (session && session.subject === subject) {
        dates.push(session.date);
        sessionMap[session.date] = session;
      }
    });
    dates = [...new Set(dates)];
    setSessionDates(dates);
    const allStudents = JSON.parse(localStorage.getItem("students")) || [];
    const filtered = allStudents.filter(
      (s) =>
        s.departmentName === studentUser.departmentName &&
        (typeof s.course === "string"
          ? s.course === (typeof studentUser.course === "string" ? studentUser.course : studentUser.course.code)
          : s.course.code === (typeof studentUser.course === "string" ? studentUser.course : studentUser.course.code))
    );
    const table = filtered.map((stu) => {
      let record = {
        roll: stu.id,
        name: stu.name,
        percentage: 0,
        marks: {},
      };
      let presentCount = 0;
      dates.forEach((d) => {
        if (sessionMap[d]?.present.includes(stu.id)) {
          record.marks[d] = "P";
          presentCount++;
        } else {
          record.marks[d] = "AB";
        }
      });
      record.percentage =
        dates.length > 0 ? Math.round((presentCount / dates.length) * 100) : 0;
      return record;
    });
    setAttendanceTable(table);
  };

  // ✅ Export to Excel
  const exportToExcel = () => {
    import("xlsx").then((XLSX) => {
      const ws = XLSX.utils.json_to_sheet(
        attendanceTable.map((row) => {
          const record = {
            "Roll No": row.roll,
            Name: row.name,
            "Total %": row.percentage,
          };
          sessionDates.forEach((d) => {
            record[d] = row.marks[d];
          });
          return record;
        })
      );
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Attendance");
      XLSX.writeFile(wb, `${selectedSubject}_Attendance.xlsx`);
    });
  };

  // ✅ Export to PDF
  const exportToPDF = async () => {
    const { default: jsPDF } = await import("jspdf");
    const autoTable = (await import("jspdf-autotable")).default;
    const doc = new jsPDF();
    doc.text(`Attendance Report - ${selectedSubject}`, 14, 15);
    autoTable(doc, {
      head: [["Roll No", "Name", "Total %", ...sessionDates]],
      body: attendanceTable.map((row) => [
        row.roll,
        row.name,
        row.percentage,
        ...sessionDates.map((d) => row.marks[d]),
      ]),
      startY: 25,
    });
    doc.save(`${selectedSubject}_Attendance.pdf`);
  };

  if (!studentUser) {
    return <p className="unauthorized">❌ Unauthorized. Please login first.</p>;
  }

  return (
    <div className="student-dashboard">
      <header className="student-header">
        <h1>🎓 Student Dashboard</h1>
        <p>
          Welcome, <strong>{studentUser?.name}</strong> (ID: {studentUser?.id})
        </p>
      </header>

      <div className="student-layout">
        {/* Sidebar */}
        <aside className="student-sidebar">
          <button
            onClick={() => {
              setViewTimetableOpen(true);
              setViewAttendanceOpen(false);
            }}
          >
            📅 View Timetable
          </button>
          <button
            onClick={() => {
              setViewAttendanceOpen(true);
              setViewTimetableOpen(false);
              setSelectedSubject(null);
              setSelectedStudent(null);
              loadStudentsForCourse();
            }}
          >
            📊 View Attendance
          </button>
          <button className="logout-btn" onClick={logoutStudent}>
            🚪 Logout
          </button>
        </aside>

        {/* Timetable Panel */}
        {viewTimetableOpen && !viewAttendanceOpen && (
          <main className="timetable-card">
            <h2>📅 Your Timetable</h2>
            {Object.keys(timetable).length === 0 ? (
              <p className="empty-msg">No timetable available</p>
            ) : (
              <table className="timetable-table">
                <thead>
                  <tr>
                    <th>Day</th>
                    {periods.map((p, i) => (
                      <th key={i}>
                        {p.label}
                        <br />({p.time})
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {days.map((day) => (
                    <tr key={day}>
                      <td>{day}</td>
                      {periods.map((p, i) => {
                        const cellData = timetable[day]?.[p.label];
                        return (
                          <td
                            key={i}
                            className={cellData ? "clickable-cell" : ""}
                            onClick={() => cellData && setQrModalOpen(true)}
                          >
                            {cellData ? (
                              <>
                                <strong>{cellData.subject}</strong>
                                <br />
                                <small>
                                  {cellData.facultyName} ({cellData.facultyId})
                                </small>
                              </>
                            ) : (
                              "-"
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </main>
        )}

        {/* Attendance Panel */}
        {viewAttendanceOpen && (
          <main className="attendance-card">
            {!selectedSubject && (
              <>
                <h2>📚 Subjects</h2>
                <ul className="subject-list">
                  {subjects.map((subject) => (
                    <li key={subject}>
                      <button
                        onClick={() => {
                          setSelectedSubject(subject);
                          buildAttendanceTable(subject);
                        }}
                      >
                        {subject}
                      </button>
                    </li>
                  ))}
                </ul>
              </>
            )}

            {selectedSubject && (
              <div className="attendance-table">
                <h2>📊 Attendance - {selectedSubject}</h2>
                {attendanceTable.length === 0 ? (
                  <p className="empty-msg">No records yet.</p>
                ) : (
                  <>
                    <table className="attendance-data">
                      <thead>
                        <tr>
                          <th>Roll No</th>
                          <th>Name</th>
                          <th>Total %</th>
                          {sessionDates.map((d, i) => (
                            <th key={i}>{d}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {attendanceTable.map((row, idx) => (
                          <tr key={idx}>
                            <td>{row.roll}</td>
                            <td>{row.name}</td>
                            <td>{row.percentage}</td>
                            {sessionDates.map((d, i) => (
                              <td key={i}>{row.marks[d]}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div className="export-buttons">
                      <button onClick={exportToExcel}>📥 Export Excel</button>
                      <button onClick={exportToPDF}>📄 Export PDF</button>
                    </div>
                  </>
                )}
              </div>
            )}
          </main>
        )}
      </div>

      {/* QR Scanner Modal */}
      {qrModalOpen && (
        <div className="modal-overlay">
          <div className="modal">
            <button className="close-btn" onClick={() => setQrModalOpen(false)}>
              ✖
            </button>
            <h2>📷 Scan Attendance QR Code</h2>
            <div className="qr-scanner-box">
              <Scanner
                onScan={handleScan}
                onError={(err) => console.error("QR Scan Error:", err)}
                components={{ audio: false }}
                constraints={{ facingMode: "environment" }}
              />
            </div>
            <div className="session-code-box">
              <h3>🔑 Or Enter Session Code</h3>
              <input
                type="text"
                value={sessionCode}
                onChange={(e) => setSessionCode(e.target.value)}
                placeholder="Enter session code"
              />
              <button className="primary-btn" onClick={handleSessionCodeSubmit}>
                Submit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Face Recognition Modal */}
      {faceModalOpen && (
        <div className="modal-overlay">
          <div className="modal">
            <button className="close-btn" onClick={() => setFaceModalOpen(false)}>
              ✖
            </button>
            <h3>😀 Face Recognition</h3>
            <p>Please align your face with the camera</p>
            <button className="primary-btn" onClick={startFaceRecognition}>
              Start Face Recognition
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
